import { CustomerContactPersonModelType } from '@teinor/erp/types/company/customer/customerContactPerson'
import { SetStateAction, useState, useCallback, useEffect } from 'react'
import { UserPermissionsSingle } from '../../../../../../../../baseComponents/CreateRoutering/types'
import LoadingSpinner from '../../../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import { ModalCreateEditStructureLayout } from '../../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import FormRenderer from '../../../../../../../../forms/FormRenderer'
import { FormRender } from '../../../../../../../../forms/FormRenderer/types'
import TwinForm from '../../../../../../../../forms/TwinForm'
import { OnSubmit } from '../../../../../../../../forms/TwinForm/types'
import { twinFetchPostJSON } from '../../../../../../../../utils/globals/data'
import { valueOrDefaultValue } from '../../../../../../../../utils/globals/valueOrDefaultValue'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { ModalCreateEditCustomerContactData } from '..'

interface SectionShareQuoteCEContactPersonProps extends SectionShareQuoteCEContactPersonLogicProps {
    userPermissions: UserPermissionsSingle | undefined
}

export const SectionShareQuoteCEContactPerson: React.FC<SectionShareQuoteCEContactPersonProps> = ({ type, setType, userPermissions, customerData, setCustomerData, onSubmit }) => {
    const { t } = useTwinTranslation()
    const { handleOnSubmit, contactPersonInstance, handleOnSubmitCreate } = useSectionShareQuoteCEContactPersonLogic({ setCustomerData, type, customerData, setType, onSubmit })
    const contactPersonFields: FormRender<CustomerContactPersonModelType> = [
        {
            cols: 1,
            elements: [
                {
                    name: 'name',
                    label: t('name', 'Nombre'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    required: true,
                },
                {
                    name: 'position',
                    label: t('position', 'Cargo'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
                {
                    name: 'CustomerId',
                    component: 'InputHidden',
                    value: customerData?.CustomerId || ''
                },
                {
                    name: 'phone',
                    label: t('phone', 'Teléfono'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
                {
                    name: 'mobile',
                    label: t('mobile', 'Móvil'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
                {
                    name: 'email',
                    label: t('email', 'Email'),
                    component: 'InputWithLabelMargin',
                    type: 'email',
                },
                {
                    name: 'notes',
                    label: t('notes', 'Notas'),
                    component: 'TextAreaStateFull',
                    value: contactPersonInstance?.notes || ''
                },
                {
                    name: 'id',
                    component: 'InputHidden',
                    value: customerData?.CustomerContactPersonId || ''
                }
            ]
        }
    ]

    if (type === 'editContactPerson') {
        if (contactPersonInstance && userPermissions) {
            const parsedFields = valueOrDefaultValue(contactPersonFields, userPermissions, contactPersonInstance)
            return (<TwinForm className='pr-30 flex flex-col flex-auto' action='/api/app/customer/customerContactPerson/updateCustomerContactPerson' onSubmit={handleOnSubmit}>
                <ModalCreateEditStructureLayout loading={false} translations={{
                    title: t('editcontactPerson', 'Editar persona de contacto'),
                    button: t('edit', 'Editar')
                }} haveButtonPermissions={true} className='modal_negative_margin'>
                    <div className='overflow-auto flex flex-auto flex-col'>
                        {parsedFields.length ?
                            <FormRenderer sections={parsedFields} />
                            : null}
                    </div>
                </ModalCreateEditStructureLayout>
            </TwinForm>)
        }
        return <LoadingSpinner />
    }
    return (<TwinForm className='pr-30 flex flex-col flex-auto' action='/api/app/customer/customerContactPerson/createCustomerContactPerson' onSubmit={handleOnSubmitCreate}>
        <ModalCreateEditStructureLayout loading={false} translations={{
            title: t('createContactPerson', 'Crear persona de contacto'),
            button: t('create', 'Crear')
        }} haveButtonPermissions={true} className='modal_negative_margin'>
            <div className='overflow-auto flex flex-auto flex-col'>
                {contactPersonFields.length ?
                    <FormRenderer sections={contactPersonFields} />
                    : null}
            </div>
        </ModalCreateEditStructureLayout>
    </TwinForm>)
}

interface SectionShareQuoteCEContactPersonLogicProps {
    customerData: ModalCreateEditCustomerContactData
    setCustomerData: React.Dispatch<SetStateAction<ModalCreateEditCustomerContactData>>
    type: string
    setType: React.Dispatch<SetStateAction<string | null>>
    onSubmit: OnSubmit
}

const useSectionShareQuoteCEContactPersonLogic = ({ type, setType, customerData, setCustomerData, onSubmit }: SectionShareQuoteCEContactPersonLogicProps) => {
    const [contactPersonInstance, setContactPersonInstance] = useState<CustomerContactPersonModelType | null>(null)
    const CustomerId = customerData.CustomerId

    const handleOnSubmitCreate: OnSubmit = useCallback(async (_res, values) => {
        const result = await twinFetchPostJSON('/api/app/customer/customerContactPerson/getAllCustomerContactPersons', { where: { name: values.name, CustomerId } })
        if (result) {
            setCustomerData((old) => {
                return { ...old, CustomerContactPersonId: result[result.length - 1].id }
            })
            onSubmit(result, {})
        }
        setType(null)
    }, [setCustomerData, setType, CustomerId, onSubmit])
    
    const getContactPersonInstance = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/customer/customerContactPerson/getCustomerContactPersonForDetail', { id: customerData.CustomerContactPersonId })
        if (result) {
            setContactPersonInstance(result)
        }
    }, [setContactPersonInstance, customerData])
    
    const handleOnSubmit: OnSubmit = useCallback(async () => {
        setType(null)
        onSubmit({} as any, {})
    }, [setType, onSubmit])

    useEffect(() => {
        if (type === 'editContactPerson') {
            getContactPersonInstance()
        }
    }, [getContactPersonInstance, type])

    return { handleOnSubmit, contactPersonInstance, handleOnSubmitCreate }
}


export default SectionShareQuoteCEContactPerson